import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Renouvellement de session transparent : si une requête renvoie 401 (token
// d'accès expiré), on tente un /auth/refresh une seule fois puis on rejoue la
// requête. En cas d'échec du refresh (session réellement morte), on laisse
// l'erreur remonter — l'AuthContext passera l'utilisateur en "déconnecté".
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");
    if (status === 401 && !original._retry && !isAuthRoute) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch (e) {
        return Promise.reject(error);
      }
    }
    if (status === 403) {
      const code = error.response?.data?.detail?.code;
      if (code === "demo_read_only" || code === "read_only" || code === "member_read_only") {
        const msg =
          error.response?.data?.detail?.message ||
          "Action indisponible en mode consultation.";
        const title =
          code === "demo_read_only" ? "Mode démo — lecture seule"
            : code === "member_read_only" ? "Collaborateur — lecture seule"
            : "Mode consultation";
        toast.error(title, { description: msg });
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fmtEUR(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
}

export function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}

export const PROPERTY_TYPES = [
  { value: "appartement", label: "Appartement" },
  { value: "maison", label: "Maison" },
  { value: "studio", label: "Studio" },
  { value: "local_commercial", label: "Local commercial" },
  { value: "parking", label: "Parking / Box" },
];

export const PROPERTY_STATUS = [
  { value: "loue", label: "Loué", color: "bg-emerald-100 text-emerald-800" },
  { value: "vacant", label: "Vacant", color: "bg-slate-100 text-slate-700" },
  { value: "travaux", label: "En travaux", color: "bg-amber-100 text-amber-800" },
];

export const LEASE_TYPES = [
  { value: "vide", label: "Location vide" },
  { value: "meuble", label: "Location meublée" },
  { value: "commercial", label: "Bail commercial" },
];

export const RECEIPT_STATUS = [
  { value: "paye", label: "Payé", color: "bg-emerald-100 text-emerald-800" },
  { value: "en_attente", label: "En attente", color: "bg-slate-100 text-slate-700" },
  { value: "partiel", label: "Partiel", color: "bg-amber-100 text-amber-800" },
  { value: "retard", label: "En retard", color: "bg-rose-100 text-rose-800" },
];
